import { Classes, InputGroup } from "@blueprintjs/core";
import { FileLink } from "modules/common/components/files/FileLink";
import { LocalLoader } from "modules/common/components/loader/Loader";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { PlanRDndUploader } from "modules/common/components/planr/dndUploader/FileDndUploader";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { normalizeQuillHtml } from "modules/common/services/quill";
import {
    DefaultSelectedOption,
    SimpleSelect,
    filterItemPredicate,
    renderSingleOptionFactory,
} from "modules/common/services/form/select";
import { useCallback, useMemo, useState } from "react";
import ReactQuill from "react-quill-new";
import { SendAgentMail } from "../models/agent-mail-store";
import { Avatar } from "./Avatar";
import { MailAgentOption, MailOrderOption } from "./mail-format";
import styles from "./AgentMailbox.module.scss";

const renderSingleOption = renderSingleOptionFactory(() => null);

/** Те же типы файлов, что и раньше в запросах агентам */
export const MAIL_ACCEPT = ["image/jpeg", "image/png", "application/pdf"].join(",");

/** Пустой редактор Quill отдаёт «<p><br></p>» */
const isEmptyHtml = (html: string) =>
    !html
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim();

interface MailComposerProps {
    /** Ответ в цепочке: договор и тема берутся из неё */
    threadKey?: string;
    /** Агенты, выбранные заранее (участники цепочки) */
    initialAgents?: string[];
    /** Договор зафиксирован (карточка договора) */
    fixedOrder?: MailOrderOption | null;
    agents: MailAgentOption[];
    categories: SelectItem[];
    orders: MailOrderOption[];
    baseUrl: string;
    /** Новое письмо на всю правую часть окна */
    full?: boolean;
    onSend: (model: SendAgentMail) => Promise<boolean>;
    onUpload: (file: File) => Promise<FileBase | null>;
    onCancel?: () => void;
}

export const MailComposer = ({
    threadKey,
    initialAgents,
    fixedOrder,
    agents,
    categories,
    orders,
    baseUrl,
    full,
    onSend,
    onUpload,
    onCancel,
}: MailComposerProps) => {
    const isReply = !!threadKey;
    const [category, setCategory] = useState<SelectItem | null>(null);
    const [selected, setSelected] = useState<string[]>(initialAgents ?? []);
    const [order, setOrder] = useState<MailOrderOption | null>(fixedOrder ?? null);
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [files, setFiles] = useState<FileBase[]>([]);
    const [sending, setSending] = useState(false);

    const withEmail = useMemo(() => agents.filter((a) => a.isAgent && !!a.email), [agents]);

    const agentItems = useMemo(
        () =>
            withEmail
                .filter((a) => !category || a.categories.includes(category.id))
                .filter((a) => !selected.includes(a.id))
                .map((a) => ({ id: a.id, label: `${a.name} <${a.email}>` })),
        [withEmail, category, selected]
    );

    const selectedAgents = useMemo(
        () => selected.map((id) => withEmail.find((a) => a.id === id)).filter((a): a is MailAgentOption => !!a),
        [selected, withEmail]
    );

    // подсказка, почему кнопка неактивна
    const problem =
        selected.length === 0
            ? "Добавьте получателей"
            : !isReply && !subject.trim()
              ? "Укажите тему"
              : isEmptyHtml(message)
                ? "Напишите текст письма"
                : "";

    const upload = useCallback(
        async (file: File) => {
            const uploaded = await onUpload(file);
            if (uploaded) {
                setFiles((current) => [...current, uploaded]);
            }
        },
        [onUpload]
    );

    const send = async () => {
        if (problem || sending) {
            return;
        }

        setSending(true);

        const ok = await onSend({
            threadKey,
            order: isReply || !order ? null : { id: order.id, name: order.label },
            agents: selected,
            subject,
            message: normalizeQuillHtml(message),
            files: files.map((f) => f.fileId),
        });

        setSending(false);

        if (ok) {
            setSubject("");
            setMessage("");
            setFiles([]);
        }
    };

    return (
        <div
            className={`${styles.composer} ${full ? styles.composerFull : ""}`}
            onKeyDown={(e) => {
                // Ctrl+Enter / Cmd+Enter - отправить, как в почтовых клиентах
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    send();
                }
            }}
        >
            {sending && <LocalLoader active text="Отправляем..." />}

            {full && <div className={styles.composerTitle}>Новое письмо</div>}

            {!isReply && (
                <div className={styles.field}>
                    <div className={styles.label}>Договор</div>
                    {fixedOrder ? (
                        <InputGroup className="planr-default-input" value={fixedOrder.label} readOnly />
                    ) : (
                        <SimpleSelect
                            className={`full-width-select ${Classes.FILL}`}
                            activeItem={order}
                            items={orders}
                            itemRenderer={renderSingleOption}
                            onItemSelect={(item: SelectItem) => setOrder(item)}
                            popoverProps={{ rootBoundary: "viewport" }}
                            inputProps={{ placeholder: "Поиск" }}
                            filterable={true}
                            itemPredicate={filterItemPredicate}
                        >
                            <DefaultSelectedOption option={order} what="Без договора" onClear={() => setOrder(null)} />
                        </SimpleSelect>
                    )}
                </div>
            )}

            <div className={styles.field}>
                <div className={styles.label}>Кому</div>
                <div className={styles.recipients}>
                    <div style={{ width: 190, flexShrink: 0 }}>
                        <SimpleSelect
                            className={`full-width-select ${Classes.FILL}`}
                            activeItem={category}
                            items={categories}
                            itemRenderer={renderSingleOption}
                            onItemSelect={(item: SelectItem) => setCategory(item)}
                            popoverProps={{ rootBoundary: "viewport" }}
                            inputProps={{ placeholder: "Поиск" }}
                            filterable={true}
                            itemPredicate={filterItemPredicate}
                        >
                            <DefaultSelectedOption
                                option={category}
                                what="Все категории"
                                onClear={() => setCategory(null)}
                            />
                        </SimpleSelect>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <SimpleSelect
                            className={`full-width-select ${Classes.FILL}`}
                            activeItem={null}
                            items={agentItems}
                            itemRenderer={renderSingleOption}
                            onItemSelect={(item: SelectItem) => setSelected((current) => [...current, item.id])}
                            popoverProps={{ rootBoundary: "viewport" }}
                            inputProps={{ placeholder: "Имя или почта агента" }}
                            filterable={true}
                            itemPredicate={filterItemPredicate}
                        >
                            <DefaultSelectedOption option={null} what="Добавить агента…" />
                        </SimpleSelect>
                    </div>
                </div>
                {selectedAgents.length > 0 && (
                    <div className={styles.recipientChips}>
                        {selectedAgents.map((agent) => (
                            <span key={agent.id} className={styles.recipient} title={agent.email ?? ""}>
                                <Avatar name={agent.name} small />
                                {agent.name}
                                <button
                                    type="button"
                                    className={styles.recipientRemove}
                                    title="Убрать из получателей"
                                    onClick={() => setSelected((current) => current.filter((id) => id !== agent.id))}
                                >
                                    <GeneralIcon type="general-cross-small" size={12} />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {!isReply && (
                <div className={styles.field}>
                    <div className={styles.label}>Тема</div>
                    <InputGroup
                        className="planr-default-input"
                        autoComplete="off"
                        placeholder="О чём письмо"
                        value={subject}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubject(e.currentTarget.value)}
                    />
                </div>
            )}

            <div className={styles.field}>
                {!isReply && <div className={styles.label}>Сообщение</div>}
                <div className={`${Classes.INPUT_GROUP} planr-default-input ${styles.editor}`}>
                    <ReactQuill
                        value={message}
                        onChange={setMessage}
                        theme="snow"
                        className="planr-default-input"
                        placeholder={isReply ? "Текст ответа…" : "Текст письма…"}
                    />
                </div>
            </div>

            <div className={styles.uploader}>
                <PlanRDndUploader accept={MAIL_ACCEPT} multiple={true} withLoader={true} onFileSelected={upload} />
                {files.length > 0 && (
                    <div className={styles.files}>
                        {files.map((file) => (
                            <FileLink
                                key={file.fileId}
                                baseUrl={baseUrl}
                                file={file}
                                onRemove={() => setFiles((current) => current.filter((f) => f.fileId !== file.fileId))}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className={styles.composerFooter}>
                <PlanrButton
                    type="greenish"
                    size="small"
                    icon="general-mail"
                    onClick={send}
                    disabled={sending || !!problem}
                    title={problem || "Ctrl+Enter"}
                >
                    {isReply ? "Ответить" : "Отправить"}
                </PlanrButton>
                {onCancel && (
                    <PlanrButton type="graybtn" size="small" onClick={onCancel}>
                        Отмена
                    </PlanrButton>
                )}
                <span className={styles.replyHint}>{problem || "Ctrl+Enter — отправить"}</span>
            </div>
        </div>
    );
};
